import React, { useState } from 'react';
import { Box, Button, Typography, useMediaQuery, useTheme } from '@mui/material';

import {
  BackdropForDialog,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  OnboardingStatusDots,
  SubmitButton,
} from 'components';
import { EOnboardingStep } from 'enums';
import { BuildingCustomizationForm, DecalForm } from 'forms';
import { useOnboardingContext } from './onboarding.context.hook';
import { DialogPrevNextClose } from './dialog-prev-next-close';
import { AutoAwesome } from '@mui/icons-material';

interface OnboardingDecalDialogProps {
  visible?: boolean;
}

export const OnboardingDecalDialog = ({ visible = false }: OnboardingDecalDialogProps) => {
  const theme = useTheme();
  const down_sm = useMediaQuery(theme.breakpoints.down('sm'));
  const [loading_form, setLoadingForm] = useState<boolean>(false);
  const [loading_submit, setLoadingSubmit] = useState<boolean>(false);
  const { setVisible, zoneId, buildingId, updateOnboarding } = useOnboardingContext();
  const [buildingCustomization, setBuildingCustomization] = useState<boolean>(false);

  const formRef = React.useRef<any>();
  const scrollRef = React.useRef<any>();

  const onClose = (event: any, reason: string) => {
    if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') {
      setVisible(false);
      document.body.removeAttribute('style');
    }
  };

  const onNext = () => {
    formRef.current.onSubmit();
  };

  const onPrev = () => {
    updateOnboarding({ status: EOnboardingStep.COMPANY_BRAND_SETTING });
  };

  const onSkip = () => {
    updateOnboarding({ status: EOnboardingStep.ASSIGN_EMPLOYEE });
  };

  const next = () => {
    updateOnboarding({ status: EOnboardingStep.ASSIGN_EMPLOYEE });
  };

  const title = 'Let’s approve your custom decal';
  const subtitle = 'Make sure everything looks good.';
  const submitButtonLabel = down_sm ? 'Next' : 'Assign a professional';

  return (
    <Dialog
      onClose={onClose}
      open={visible}
      PaperProps={{ sx: { marginBottom: 12, ...(down_sm ? {} : { overflow: 'visible' }) } }}
    >
      <DialogTitle>
        <DialogPrevNextClose onPrev={onPrev} onNext={onSkip} onClose={onClose} />

        <Box>
          <Typography variant="subtitle2" color="textSecondary">
            Step 4 of 6
          </Typography>
        </Box>

        <Box sx={{ marginTop: 2 }}>
          <Typography variant="h5">{title}</Typography>

          <Typography
            variant="body2"
            color="textSecondary"
            sx={{
              marginTop: 1,
            }}
          >
            {subtitle}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent ref={scrollRef}>
        <Box>
          {zoneId ? (
            <DecalForm
              ref={formRef}
              zoneId={zoneId as any}
              onInit={(loading) => setLoadingForm(loading)}
              onSubmitting={(loading) => setLoadingSubmit(loading)}
              next={next}
            />
          ) : (
            <Typography color="textSecondary">No zone selected</Typography>
          )}
        </Box>
      </DialogContent>

      {!buildingCustomization && (
        <Box sx={{ paddingX: down_sm ? 3 : 8, paddingBottom: 2 }}>
          <Button sx={{ paddingX: 2 }} startIcon={<AutoAwesome />} onClick={() => setBuildingCustomization(true)}>
            Need to customize this decal?
          </Button>
        </Box>
      )}

      {buildingCustomization && (
        <Box
          sx={{
            marginTop: 6,
            paddingX: down_sm ? 3 : 8,
            paddingBottom: 3,
            bgcolor: (theme) => theme.palette.grey[100],
            position: 'relative',
          }}
        >
          <BuildingCustomizationForm buildingId={buildingId as number} next={() => setBuildingCustomization(false)} />
        </Box>
      )}

      {zoneId && (
        <DialogActions sx={{ paddingTop: 6 }}>
          <SubmitButton loading={loading_submit} label={submitButtonLabel} onClick={onNext} />
        </DialogActions>
      )}

      <Box
        sx={{
          width: '100%',
          left: 0,
          display: 'flex',
          justifyContent: 'center',
          ...(down_sm ? { paddingBottom: 6 } : { position: 'absolute', bottom: -60 }),
        }}
      >
        <OnboardingStatusDots status={EOnboardingStep.APPROVE_DECAL} />
      </Box>

      <BackdropForDialog loading={loading_form} hideSpinner />
    </Dialog>
  );
};
